import Title from "components/Title";
import { RootState } from "redux/store";
import useToggle from "hooks/useToggle";
import Spinner from "components/Spinner";
import { useEffect, useState, useRef } from "react";
import { FormInput } from "components/Form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
	Card,
	Col,
	Row,
	Form,
	Button,
	Alert,
	Accordion,
} from "react-bootstrap";
import { getPermissionNames } from "redux/slices/Permissions";
import {
	getAccountsTable,
	getStoreGroups,
	createAccount,
	updateAccount,
	cleanAccountDetails,
	updateAccountField,
	getAccountDetails,
} from "redux/slices/Accounts";
import { getUserDetails } from "redux/slices/User";
import { TooltipIcon } from "components/Tooltip";
import { User } from "models/dbModels/users/User";
import { getFeatureFlagValue } from "components/ZFeatureFlag";
import { DataResponse } from "models/common/responseObjects";

export interface childLevel {
	title: string;
	key: string;
	display: string[];
	tooltip?: string;
	disabled: string[];
}

export interface parentLevel {
	title: string;
	children: childLevel[];
}

export const permissionsMenu: parentLevel[] = [
	{
		title: "Dashboard",
		children: [
			{ title: "Dashboard", key: "dashboard", display: ["View"], disabled: [] },
		],
	},

	{
		title: "Stocks",
		children: [
			{
				title: "Managed Stock",
				key: "managedStock",
				display: ["View", "Create", "Update", "Delete", "Import"],
				disabled: [],
			},
			{
				title: "Suppliers",
				key: "suppliers",
				display: ["View", "Create", "Update", "Delete"],
				disabled: [],
			},
			{
				title: "Departments",
				key: "departments",
				display: ["View", "Create", "Update", "Delete", "Import"],
				disabled: [],
			},
			{
				title: "Stock Groups",
				key: "stockGroups",
				display: ["View", "Create", "Update", "Delete"],
				disabled: [],
			},
			{
				title: "Manufacturers",
				key: "manufacturers",
				display: ["View", "Create", "Update", "Delete"],
				disabled: [],
			},
			{
				title: "Store Data",
				key: "functions",
				display: ["View"],
				disabled: [],
				tooltip: "Permissions to view store data such as stock information",
			},
		],
	},

	{
		title: "Multi-Level Pricing",
		children: [
			{
				title: "Price Groups",
				key: "priceGroups",
				display: ["View", "Create", "Update", "Delete", "Import"],
				disabled: [],
			},
			{
				title: "Assign Price Groups",
				key: "assignPriceGroups",
				display: ["View", "Update"],
				disabled: [],
			},
		],
	},

	{
		title: "Dispense",
		children: [
			{
				title: "Special Pricing",
				key: "dispensePricing",
				display: ["View", "Update", "Import"],
				disabled: [],
			},
			{
				title: "Preferred Brands",
				key: "preferredBrands",
				display: ["View", "Create", "Update", "Delete", "Import"],
				disabled: [],
			},
		],
	},

	{
		title: "Promotions",
		children: [
			{
				title: "Catalogues",
				key: "promotions",
				display: ["View", "Create", "Update", "Delete", "Import"],
				disabled: [],
			},
		],
	},

	{
		title: "Supplier Deals",
		children: [		],
	},

	{
		title: "Store Management", children: [
			{ title: "Stores", key: "stores", display: ['View'], disabled: [] },
			{ title: "Store Groups", key: "storeGroups", display: ['View', "Create", "Update", "Delete"], disabled: [] },
		]
	},
	{
		title: "Reports", children: [
			{ title: "Store Reports", key: "reports", display: ["View"], disabled: []},
		]
	},

	{
		title: "Store Management",
		children: [
			{
				title: "Stores",
				key: "stores",
				display: ["View", "Update"],
				disabled: [],
			},
			{
				title: "Store Groups",
				key: "storeGroups",
				display: ["View", "Create", "Update", "Delete"],
				disabled: [],
			},
		],
	},

	{
		title: "Admin",
		children: [
			{
				title: "Accounts",
				key: "accounts",
				display: ["View", "Create", "Update"],
				disabled: [],
			},
		],
	},
];

export function NewAccount() {
	const form = useRef(null);
	const navigate = useNavigate();
	const { accountId } = useParams();
	const dispatch = useAppDispatch();
	const [isValidated, setValidated] = useToggle();
	const [showPassword, togglePassword] = useToggle();
	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const account = useAppSelector((state: RootState) => state.account);
	const global = useAppSelector((state: RootState) => state.global);
	const [isNewAccount, setIsNewAccount] = useState<boolean>(true);
	const [isPasswordRequired, setIsPasswordRequired] = useState<boolean>(false);
	const currentStoreGroupId = useAppSelector(
		(state: RootState) => state.global.storeGroupId
	);
	const permissions = useAppSelector((state: RootState) => state.permissions);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const [userPermissions, setUserPermissions] = useState(permissionsMenu);

	useEffect(() => {
		dispatch(getStoreGroups());
		dispatch(getPermissionNames()).then((res) => {
			let temp = [...userPermissions];
			let others: childLevel[] = [];

			let flatMap = temp.flatMap((i) => i.children);

			res.data.forEach((permissionName: string) => {
				if (
					flatMap.find(
						(i) => i.key.toLowerCase() === permissionName.toLowerCase()
					)
				) {
					//All good, we have it already
				} else {
					others.push({
						title: permissionName,
						key:
							permissionName.charAt(0).toLowerCase() + permissionName.slice(1),
						display: ["View", "Create", "Update", "Delete", "Import"],
						disabled: [],
					});
				}
			});

			// // Uncomment during development
			// if (others.length > 0) {
			// 	temp.push({
			// 		title: "Other", children: [...others]
			// 	});
			// }

			//Same user!
			let adminOption = temp.find((i) => i.title === "Admin");
			if (adminOption) {
				if (accountId?.toString() === user.id.toString()) {
					adminOption.children[0].disabled = ["View", "Update"];
				} else {
					adminOption.children[0].disabled = [];
				}
			}

			setUserPermissions(temp);
		});

		if (accountId) {
			const accountIdNum = Number.parseInt(accountId);

			if (!isNaN(accountIdNum)) {
				dispatch(getAccountDetails(accountIdNum));
				setIsNewAccount(false);
			} else {
				navigate("/notfound");
			}
		} else {
			dispatch(cleanAccountDetails());
			setIsNewAccount(true);

			//New Account? Check all!
			let tempPermission: any = { ...account.permissions };

			let temp = [...userPermissions];
			temp.forEach((element) => {
				element.children.forEach((child) => {
					child.display.forEach((display) => {
						//@ts-ignore
						let typeTemp = { ...tempPermission[child.key] };
						typeTemp["can" + display] = true;
						tempPermission[child.key] = typeTemp;
					});
				});
			});

			handleChange("permissions", tempPermission);
		}
	}, [accountId]);

	useEffect(() => {
		let temp = [...permissionsMenu];
		
		
		const showLockdownFields = getFeatureFlagValue(global.featureFlags, "ZFreedom.Stores.LockDownFields") === true;
		if (showLockdownFields) {
			let lockDownField = temp.find(i => i.title === 'Store Management');
			if (lockDownField) {
				if (!lockDownField.children.find(i => i.key === 'lockDownFields')) {
					lockDownField.children.push(
						{ title: "Lockdown", key: "lockDownFields", display: ['View', "Update"], disabled: [] },
					);
				}
			}
		}


		const showStockDeals = getFeatureFlagValue(global.featureFlags, "ZFreedom.SupplierDeals.StockDeals") === true;
		const showAssortedDeals = getFeatureFlagValue(global.featureFlags, "ZFreedom.SupplierDeals.AssortedDeals") === true;
		if (showStockDeals || showAssortedDeals) {
			let supplierDeals = temp.find(i => i.title === 'Supplier Deals');
			if (supplierDeals) {
				if (showStockDeals && !supplierDeals.children.find(i => i.key === 'supplierDeals')) {
					supplierDeals.children.push(
						{
							title: "Stock Deals",
							key: "supplierDeals",
							display: ["View", "Create", "Update", "Delete", "Import"],
							disabled: [],
						});
					}

				if (showAssortedDeals && !supplierDeals.children.find(i => i.key === 'supplierGroupDeals')) {
					supplierDeals.children.push(
						{
							title: "Assorted Deals",
							key: "supplierGroupDeals",
							display: ["View", "Create", "Update", "Delete", "Import"],
							disabled: [],
						});
				}
			}
		}
		
		setUserPermissions(temp);
	}, [global.featureFlags])

	const storeGroupSelectOptions: any = (items: any) => {
		return items.map((i: any, index: any) => {
			<option
				key={index}
				value={i.id.toString()}
				selected={account.storeGroupId.toString() === i.id.toString()}
			>
				{i.name}
			</option>;
		});
	};

	function handleSubmit() {
		if (isNewAccount === true) setIsPasswordRequired(true);
		if (account.isPasswordChanged) {
			if (account.password !== account.confirmPassword)
				alert("Password and Confirm Password must be the same");
		}
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const newUser: User = {
				id: account.id,
				email: account.email,
				givenName: account.givenName,
				familyName: account.familyName,
				status: account.status,
				comments: "",
				access: 1,
				permissions: account.permissions,
				storeGroupId: 0,
			};
			if (isNewAccount) {
				dispatch(createAccount(newUser)).then(() => redirectToTable());
			} else {
				dispatch(updateAccount(newUser)).then(() => redirectToTable());
			}
		}
		setValidated();
	}

	function redirectToTable() {
		//Reload the permissions
		if (account.id.toString() === user.id.toString()) {
			//Same user!
			dispatch(getUserDetails());
		}

		dispatch(getAccountsTable());
		navigate("/app/accounts");
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateAccountField({ key: option, value: optionValue }));
	}

	function getValue(key: string, type: string) {
		let permissions = account.permissions as any;
		if (permissions[key]) {
			return permissions[key][type] as boolean;
		}
		return false;
	}

	function setValue(key: string, type: string, checked: boolean) {
		//@ts-ignore
		let typeTemp = { ...account.permissions[key] };
		typeTemp[type] = checked;

		let temp: any = { ...account.permissions };
		temp[key] = typeTemp;

		handleChange("permissions", temp);
	}

	function setMultiValue(
		children: childLevel[],
		typeDisplay: string,
		type: string,
		checked: boolean
	) {
		let temp: any = { ...account.permissions };

		children.forEach((i) => {
			if (
				i.display.includes(typeDisplay) &&
				!i.disabled.includes(typeDisplay)
			) {
				//@ts-ignore
				let typeTemp = { ...account.permissions[i.key] };
				typeTemp[type] = checked;

				temp[i.key] = typeTemp;
			}
		});

		handleChange("permissions", temp);
	}

	function getMultiValue(
		children: childLevel[],
		typeDisplay: string,
		type: string
	): boolean {
		let filtered = children.filter(
			(i) =>
				i.display.includes(typeDisplay) && !i.disabled.includes(typeDisplay)
		);
		let checked =
			filtered.length > 0
				? filtered.every((c) => getValue(c.key, type))
				: false;

		return checked;
	}

	function CanSave(): boolean {
		return (
			(isNewAccount && user.permissions.accounts.canCreate) ||
			(!isNewAccount && user.permissions.accounts.canUpdate)
		);
	}

	return (
		<>
			<Title
				pageTitle={isNewAccount ? "New Account" : "Update Account"}
				crumbs={[{ name: "Accounts", path: "/app/accounts" }]}
			/>
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Form validated={isValidated} ref={form}>
									<Row>
										<h4 className="mt-2">Account Details</h4>
										<p className="text-muted">
											Fill the form below create a new account. User will be
											sent an email to create their password.
										</p>
										{account.error && (
											<p>
												<Alert variant="warning">{account.error}</Alert>
											</p>
										)}
										<Col md={6}>
											<FormInput
												label="First Name"
												type="text"
												name="account-given-name"
												placeholder="Enter first name"
												containerClass={"mb-3"}
												required
												value={account.givenName}
												onChange={(e) =>
													handleChange("givenName", e.target.value)
												}
											/>
										</Col>
										<Col md={6}>
											<FormInput
												label="Last Name"
												type="text"
												name="account-family-name"
												placeholder="Enter last name"
												containerClass={"mb-3"}
												required
												value={account.familyName}
												onChange={(e) =>
													handleChange("familyName", e.target.value)
												}
											/>
										</Col>
									</Row>
									<Row>
										<Col md={6}>
											<div className="form-group">
												<FormInput
													label="Email"
													type="email"
													name="account-email"
													placeholder="Enter email"
													containerClass={"mb-3"}
													required
													value={account.email}
													onChange={(e) =>
														handleChange("email", e.target.value)
													}
												/>
											</div>
										</Col>
										<Col md={6}>
											{!isNewAccount && (
												<Form.Group>
													<Form.Label
														className="form-check-label"
														htmlFor="exampleCheckbox"
													>
														Status
													</Form.Label>
													<div>
														<Form.Check
															type="switch"
															id="stock-active"
															label={account.status}
															checked={account.status === "Active" && true}
															onChange={(e) => {
																if (account.status === "Active") {
																	handleChange("status", "Suspended");
																} else {
																	handleChange("status", "Active");
																}
															}}
														/>
													</div>
												</Form.Group>
											)}
										</Col>
									</Row>

									<Row>
										<Accordion>
											<Accordion.Item eventKey="0">
												<Accordion.Header>Permissions</Accordion.Header>
												<Accordion.Body>
													{userPermissions.map((p, i) => (
														<div key={i} className="pb-3">
															{p.children.length > 0 && (
																<Row className="border pt-2 bg-body-tertiary ">
																	<Col md={2}>
																		<h4>{p.title}</h4>
																	</Col>

																	<Col md={2}>
																		<Form.Group
																			hidden={
																				!p.children.some(
																					(i) =>
																						i.display.includes("View") &&
																						!i.disabled.includes("View")
																				)
																			}
																		>
																			<Form.Check
																				type="checkbox"
																				label="View"
																				checked={getMultiValue(
																					p.children,
																					"View",
																					"canView"
																				)}
																				onChange={(e) =>
																					setMultiValue(
																						p.children,
																						"View",
																						"canView",
																						e.currentTarget.checked
																					)
																				}
																			/>
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group
																			hidden={
																				!p.children.some(
																					(i) =>
																						i.display.includes("Create") &&
																						!i.disabled.includes("Create")
																				)
																			}
																		>
																			<Form.Check
																				type="checkbox"
																				label="Create"
																				checked={getMultiValue(
																					p.children,
																					"Create",
																					"canCreate"
																				)}
																				onChange={(e) =>
																					setMultiValue(
																						p.children,
																						"Create",
																						"canCreate",
																						e.currentTarget.checked
																					)
																				}
																			/>
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group
																			hidden={
																				!p.children.some(
																					(i) =>
																						i.display.includes("Update") &&
																						!i.disabled.includes("Update")
																				)
																			}
																		>
																			<Form.Check
																				type="checkbox"
																				label="Update"
																				checked={getMultiValue(
																					p.children,
																					"Update",
																					"canUpdate"
																				)}
																				onChange={(e) =>
																					setMultiValue(
																						p.children,
																						"Update",
																						"canUpdate",
																						e.currentTarget.checked
																					)
																				}
																			/>
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group
																			hidden={
																				!p.children.some(
																					(i) =>
																						i.display.includes("Delete") &&
																						!i.disabled.includes("Delete")
																				)
																			}
																		>
																			<Form.Check
																				type="checkbox"
																				label="Delete"
																				checked={getMultiValue(
																					p.children,
																					"Delete",
																					"canDelete"
																				)}
																				onChange={(e) =>
																					setMultiValue(
																						p.children,
																						"Delete",
																						"canDelete",
																						e.currentTarget.checked
																					)
																				}
																			/>
																		</Form.Group>
																	</Col>
																	<Col md={2}>
																		<Form.Group
																			hidden={
																				!p.children.some(
																					(i) =>
																						i.display.includes("Import") &&
																						!i.disabled.includes("Import")
																				)
																			}
																		>
																			<Form.Check
																				type="checkbox"
																				label="Import"
																				checked={getMultiValue(
																					p.children,
																					"Import",
																					"canImport"
																				)}
																				onChange={(e) =>
																					setMultiValue(
																						p.children,
																						"Import",
																						"canImport",
																						e.currentTarget.checked
																					)
																				}
																			/>
																		</Form.Group>
																	</Col>
																</Row>
															)}
															{p.children.length > 0 &&
																p.children.map((c, ii) => (
																	<div key={ii}>
																		<Row className="my-1">
																			<Col md={2}>
																				<p className="text-muted ms-1">
																					{c.title}
																					{c.tooltip && (
																						<TooltipIcon
																							text={c.tooltip}
																							placement="right"
																						/>
																					)}
																				</p>
																			</Col>
																			<Col md={2}>
																				<Form.Group
																					hidden={!c.display.includes("View")}
																				>
																					<Form.Check
																						type="checkbox"
																						label="View"
																						disabled={c.disabled?.includes(
																							"View"
																						)}
																						checked={getValue(c.key, "canView")}
																						onChange={(e) =>
																							setValue(
																								c.key,
																								"canView",
																								e.currentTarget.checked
																							)
																						}
																					/>
																				</Form.Group>
																			</Col>
																			<Col md={2}>
																				<Form.Group
																					hidden={!c.display.includes("Create")}
																				>
																					<Form.Check
																						type="checkbox"
																						label="Create"
																						disabled={c.disabled?.includes(
																							"Create"
																						)}
																						checked={getValue(
																							c.key,
																							"canCreate"
																						)}
																						onChange={(e) =>
																							setValue(
																								c.key,
																								"canCreate",
																								e.currentTarget.checked
																							)
																						}
																					/>
																				</Form.Group>
																			</Col>
																			<Col md={2}>
																				<Form.Group
																					hidden={!c.display.includes("Update")}
																				>
																					<Form.Check
																						type="checkbox"
																						label="Update"
																						disabled={c.disabled?.includes(
																							"Update"
																						)}
																						checked={getValue(
																							c.key,
																							"canUpdate"
																						)}
																						onChange={(e) =>
																							setValue(
																								c.key,
																								"canUpdate",
																								e.currentTarget.checked
																							)
																						}
																					/>
																				</Form.Group>
																			</Col>
																			<Col md={2}>
																				<Form.Group
																					hidden={!c.display.includes("Delete")}
																				>
																					<Form.Check
																						type="checkbox"
																						label="Delete"
																						disabled={c.disabled?.includes(
																							"Delete"
																						)}
																						checked={getValue(
																							c.key,
																							"canDelete"
																						)}
																						onChange={(e) =>
																							setValue(
																								c.key,
																								"canDelete",
																								e.currentTarget.checked
																							)
																						}
																					/>
																				</Form.Group>
																			</Col>
																			<Col md={2}>
																				<Form.Group
																					hidden={!c.display.includes("Import")}
																				>
																					<Form.Check
																						type="checkbox"
																						label="Import"
																						disabled={c.disabled?.includes(
																							"Import"
																						)}
																						checked={getValue(
																							c.key,
																							"canImport"
																						)}
																						onChange={(e) =>
																							setValue(
																								c.key,
																								"canImport",
																								e.currentTarget.checked
																							)
																						}
																					/>
																				</Form.Group>
																			</Col>
																		</Row>
																	</div>
																))}
														</div>
													))}
												</Accordion.Body>
											</Accordion.Item>
										</Accordion>
									</Row>
									{/* {isNewAccount ? <PasswordRequirements /> : null} */}
									<Row className="mt-3">
										<Col sm={6}>
											<Link
												to="/app/accounts"
												className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold"
											>
												<i className="mdi mdi-arrow-left"></i> Back to All
												Accounts{" "}
											</Link>
										</Col>
										<Col
											sm={6}
											className="d-flex flex-row-reverse"
											data-toggle="tooltip"
											data-placement="bottom"
											title={
												!CanSave()
													? "User does not have permission to save."
													: undefined
											}
										>
											<Button
												variant={!CanSave() ? "disabled" : "primary"}
												className="btn cta-button"
												disabled={loading || !CanSave()}
												onClick={handleSubmit}
											>
												{loading ? (
													<>
														<Spinner
															className="spinner-border-sm me-1"
															tag="span"
															color="white"
														/>
													</>
												) : (
													<></>
												)}
												{loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
								</Form>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}
